import { InfoOutlined } from 'components/icons';
import { Alert } from 'components/ui/alert';
import { getIntl, t } from 'utils/getTranslations';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import { ReportEntry, rowLimitOf } from './reportCatalog';

type Props = {
    report: ReportEntry;
    /** The rows the report answered, `undefined` until it has. */
    rowCount: number | undefined;
};

/**
 * Says that the list stops at the report's row limit while its totals row covers every row, so totals above the
 * visible rows do not read as wrong. The backend sends no count of the rows past the limit, which would cost a scan
 * of them all, so a list that fills the limit exactly gets the note too: it still shows only the first rows. The
 * limit is written as the list header writes its row count, with a comma between thousands.
 */
export function ReportRowLimitNote({ report, rowCount }: Props) {
    const limit = rowLimitOf(report);
    if (rowCount === undefined || rowCount < limit) {
        return null;
    }
    return (
        <Alert
            role="status"
            className="bg-info/8 text-foreground rounded-md border-transparent py-0.5 pr-4 pl-3 has-[>svg]:gap-x-2"
        >
            <InfoOutlined className="text-info! size-[18px]" />
            {t('reporting.row-limit-note', {
                limit: isNewDesignCopy() ? getIntl().formatNumber(limit) : limit.toLocaleString('en-US'),
            })}
        </Alert>
    );
}
